// ManufacturerBrandDrillDown.tsx
//
// Data-quality drill-down: given a manufacturer + a brand (a chip clicked in any
// Data Health surface), shows EXACTLY which of that brand's references link the
// brand into the manufacturer's derived brand set — i.e. the refs whose `calibre`
// string resolves to a calibre in this manufacturer's set (primary manufacturerId
// + additionalManufacturerIds). This is the exact set `deriveManufacturerBrands`
// folds into `brandIdsDerivedFromRefs`, so the drill-down and the derivation agree.
//
// Match semantics come from the shared resolver (services/calibreResolution.ts)
// — brand-scoped AKAs and the placeholder blacklist — with ONE narrowing added
// here: catch-all calibres are skipped entirely (see buildGroups), because the
// resolver lets their brand-scoped aliases match and the derivation does not.
// Each ref row reports the match METHOD —
// canonical name vs alias — which is the signal for investigating pollution (e.g.
// a brand-scoped AKA that leaked into another brand's refs).
//
// Fully client-side + read-only for the scan (a full read of the brand's refs +
// the manufacturer's calibres). Clicking a ref opens the existing ReferenceEditor
// inline (swapping the modal body), reusing the CrossBrandCalibreSearch pattern.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { WatchBrand, WatchReference } from '../../types';
import {
  getAllReferencesForBrand,
  getCalibresForManufacturerIncludingCoDeveloped,
  getWatchReference,
  type CustomCalibreEntry,
} from '../../services/firebase';
import {
  calibreRefMatchMethod,
  isCatchAllCalibre,
  type CalibreRefMatch,
} from '../../services/calibreResolution';
import { Modal } from '../common/Modal';
import { ReferenceEditor } from '../CuratedWatches/ReferenceEditor';

/** One matched reference within a calibre group. */
interface DrillDownRefRow {
  ref: WatchReference;
  match: CalibreRefMatch;
}

/** Refs matched via a single calibre in the manufacturer's set. */
interface CalibreGroup {
  calibre: CustomCalibreEntry;
  rows: DrillDownRefRow[];
}

/**
 * Scan `refs` (one brand's references) against `calibres` (a manufacturer's set),
 * grouping matched refs by the calibre they resolved to. A ref that matches more
 * than one of the manufacturer's calibres appears in each group (informative — it
 * flags an ambiguous calibre string). Groups are sorted by ref count desc, then
 * calibre name; rows within a group by reference string.
 */
function buildGroups(
  calibres: CustomCalibreEntry[],
  refs: WatchReference[],
  brandId: string,
): CalibreGroup[] {
  const groups: CalibreGroup[] = [];
  for (const calibre of calibres) {
    // Derivation is stricter than matching, so this cannot lean on the match
    // rules to exclude a catch-all for it. `calibreMatchKeysForBrand` lets a
    // catch-all's BRAND-SCOPED aliases match — right for enrichment, wrong here
    // — while deriveManufacturerBrands drops catch-all calibres outright, on
    // the FK path and the string path alike. Skipping them keeps this list
    // exactly the set the server folds into `brandIdsDerivedFromRefs`; without
    // it the drill-down would show refs the derivation never counted, which
    // reads as a bug in the derivation rather than in the drill-down.
    if (isCatchAllCalibre(calibre)) continue;
    const rows: DrillDownRefRow[] = [];
    for (const ref of refs) {
      const calibreStr = (ref.calibre || '').trim();
      if (!calibreStr) continue;
      const match = calibreRefMatchMethod(calibre, calibreStr, brandId);
      if (match) rows.push({ ref, match });
    }
    if (rows.length === 0) continue;
    rows.sort((a, b) => (a.ref.reference || '').localeCompare(b.ref.reference || ''));
    groups.push({ calibre, rows });
  }
  groups.sort(
    (a, b) =>
      b.rows.length - a.rows.length ||
      (a.calibre.name || '').localeCompare(b.calibre.name || ''),
  );
  return groups;
}

function MatchBadge({ match }: { match: CalibreRefMatch }) {
  const isCanonical = match.via === 'canonical';
  return (
    <span
      title={
        isCanonical
          ? `Matched the calibre's canonical name "${match.matchedName}"`
          : `Matched via alias "${match.matchedName}"`
      }
      className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
        isCanonical
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-amber-200 bg-amber-50 text-amber-800'
      }`}
    >
      {isCanonical ? 'canonical' : `AKA "${match.matchedName}"`}
    </span>
  );
}

export function ManufacturerBrandDrillDown({
  isOpen,
  onClose,
  manufacturerId,
  manufacturerName,
  brandId,
  brandsById,
}: {
  isOpen: boolean;
  onClose: () => void;
  manufacturerId: string;
  manufacturerName: string;
  brandId: string;
  brandsById: Map<string, WatchBrand>;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<CalibreGroup[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // When set, the modal body swaps to the reference editor for this ref.
  const [editing, setEditing] = useState<{ reference: WatchReference; brand: WatchBrand } | null>(
    null,
  );
  const [openingRefId, setOpeningRefId] = useState<string | null>(null);
  // Bridge to the editor's unsaved-changes guard so the outer Modal's
  // Esc/backdrop close and the "← Back to refs" link route through the same
  // confirm dialog while a ref edit is dirty. Only set while editing a ref.
  const editorExitGuardRef = useRef<((proceed: () => void) => void) | null>(
    null,
  );
  const guardedExit = useCallback((proceed: () => void) => {
    const guard = editorExitGuardRef.current;
    if (guard) guard(proceed);
    else proceed();
  }, []);

  const brand = brandsById.get(brandId);
  const brandName = brand?.displayName || brand?.name || brandId;

  const scan = useCallback(async () => {
    if (!brandId || !manufacturerId) return;
    setLoading(true);
    setError(null);
    try {
      const [calibres, refs] = await Promise.all([
        getCalibresForManufacturerIncludingCoDeveloped(manufacturerId),
        getAllReferencesForBrand(brandId),
      ]);
      setGroups(buildGroups(calibres, refs, brandId));
    } catch (err) {
      console.error('[BrandDrillDown] scan failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load references');
    } finally {
      setLoading(false);
    }
  }, [brandId, manufacturerId]);

  // (Re)scan whenever the modal opens or the target brand changes; reset view.
  useEffect(() => {
    if (!isOpen) return;
    setEditing(null);
    setCollapsed(new Set());
    void scan();
  }, [isOpen, scan]);

  const totalRefs = useMemo(
    () => new Set(groups.flatMap((g) => g.rows.map((r) => r.ref.id))).size,
    [groups],
  );

  const openRef = async (ref: WatchReference) => {
    if (!brand) {
      toast.error('Brand doc not loaded — cannot open the editor');
      return;
    }
    setOpeningRefId(ref.id);
    try {
      // Fetch the freshest copy so edits round-trip correctly (matches the
      // cross-brand calibre search open pattern).
      const fresh = (await getWatchReference(brandId, ref.id)) ?? ref;
      setEditing({ reference: fresh, brand });
    } catch (err) {
      console.error('[BrandDrillDown] failed to open ref', err);
      toast.error('Failed to open reference');
    } finally {
      setOpeningRefId(null);
    }
  };

  const toggleGroup = (calibreId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(calibreId)) next.delete(calibreId);
      else next.add(calibreId);
      return next;
    });

  const title = editing
    ? `${brandName} · ${editing.reference.reference || 'reference'}`
    : `${brandName} refs using ${manufacturerName}'s calibres`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => guardedExit(onClose)}
      title={title}
      size="full"
    >
      {editing ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => guardedExit(() => setEditing(null))}
            className="text-sm text-brand-600 hover:underline"
          >
            ← Back to {brandName} refs
          </button>
          <ReferenceEditor
            key={editing.reference.id}
            reference={editing.reference}
            brand={editing.brand}
            exitGuardRef={editorExitGuardRef}
            onBack={() => setEditing(null)}
            onSaved={() => {
              // A calibre edit can change membership — re-scan on return.
              setEditing(null);
              void scan();
            }}
            onDeleted={() => {
              setEditing(null);
              void scan();
            }}
          />
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div className="space-y-3 py-6 text-center">
          <p className="text-sm text-rose-700">{error}</p>
          <button
            type="button"
            onClick={() => void scan()}
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            Retry
          </button>
        </div>
      ) : totalRefs === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            No refs currently link {brandName} to {manufacturerName}.
          </p>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
            The stored derived brand set may be stale — re-run the derivation
            (deriveManufacturerBrands) to refresh it.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">{totalRefs}</span> ref
            {totalRefs === 1 ? '' : 's'} found across{' '}
            <span className="font-semibold">{groups.length}</span> calibre
            {groups.length === 1 ? '' : 's'}. Each ref&apos;s stored{' '}
            <code className="font-mono text-xs">calibre</code> resolves to one of{' '}
            {manufacturerName}&apos;s calibres via the shared resolver — the same
            match the derivation uses.
          </p>

          {groups.map(({ calibre, rows }) => {
            const isCollapsed = collapsed.has(calibre.id);
            return (
              <div key={calibre.id} className="rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => toggleGroup(calibre.id)}
                  className="flex w-full items-center gap-2 rounded-t-lg bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
                >
                  <span className="w-3 text-xs text-gray-400">
                    {isCollapsed ? '▶' : '▼'}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    Calibre: {calibre.name || calibre.id}
                  </span>
                  {calibre.manufacturerId !== manufacturerId && (
                    <span
                      title="This manufacturer is a co-developer (additionalManufacturerIds), not the primary maker of this calibre."
                      className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                    >
                      co-developed
                    </span>
                  )}
                  <span className="ml-auto text-xs text-gray-500">
                    {rows.length} ref{rows.length === 1 ? '' : 's'}
                  </span>
                </button>

                {!isCollapsed && (
                  <ul className="divide-y divide-gray-100">
                    {rows.map(({ ref, match }) => (
                      <li key={ref.id}>
                        <button
                          type="button"
                          onClick={() => void openRef(ref)}
                          disabled={openingRefId === ref.id}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-brand-50/50 disabled:opacity-50"
                        >
                          <span className="inline-flex shrink-0 items-center rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700">
                            {brandName}
                          </span>
                          <span className="shrink-0 font-medium text-gray-900">
                            {ref.reference || '(no ref #)'}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm text-gray-600">
                            {ref.model || ref.productName || '—'}
                          </span>
                          <span
                            title={`Stored calibre string: "${ref.calibre}"`}
                            className="hidden max-w-[180px] shrink-0 truncate text-xs text-gray-400 sm:block"
                          >
                            {ref.calibre}
                          </span>
                          <MatchBadge match={match} />
                          {openingRefId === ref.id && (
                            <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-b-2 border-brand-600" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
